import { IS_BIZ_USER, IS_CORP_USER, IS_PAID_USER, IS_SOCIAL_USER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    extendQuotaOffer,
    extendQuotaOfferLimit,
    extendQuotaOfferShowInterval,
    extendQuotaOfferTariffs,
} from 'reactApp/appHelpers/featuresHelpers/features/extendQuotaOffer';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { loadSubscriptionProducts } from 'reactApp/modules/billingData/billingData.provider';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openFastCheckout } from 'reactApp/modules/payment/payment.module';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { hasMonthTariff } from 'reactApp/modules/products/products.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { goToPromoQuota } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store as reduxStore } from 'reactApp/store';
import {
    getExtentQuotaOfferTitle,
    preloadExtentQuotaOfferCoverImage,
    renderExtendQuotaOfferMobileModal,
    renderExtendQuotaOfferModal,
} from 'reactApp/ui/ExtendQuotaOfferModal/ExtendQuotaOfferModal.helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { call, put, select, take } from 'typed-redux-saga';

const PROMO_ID = 'extend-offer-promo';
const RADAR_NAME = 'mnz_completed_paid_quota';
const PAY_SOURCE = 'completed_paid_quota';

export function* initExtendQuotaOfferModal() {
    const storage = yield* select(getCurrentStorage);

    const { isHome } = getStorage(storage);

    const { isLoaded: isUserQuotaLoaded } = yield* select(UserQuotaSelectors.getLoadingState);
    if (!isUserQuotaLoaded) {
        yield take(loadUserQuotaSuccess);
    }

    const { used: usedPercent } = yield select(UserQuotaSelectors.getFilledPercents);

    const showCount: number = storeHelper.getValue(PROMO_ID) || 0;
    const periodPassed = storeHelper.isPassed(PROMO_ID, { daysCount: extendQuotaOfferShowInterval });

    if (
        !isHome ||
        IS_CORP_USER ||
        IS_BIZ_USER ||
        IS_SOCIAL_USER ||
        !IS_PAID_USER ||
        !extendQuotaOffer ||
        usedPercent < extendQuotaOfferLimit ||
        (showCount && !periodPassed)
    ) {
        return;
    }

    yield put(
        addPromoToShowQueue({
            type: EPromoType.extendQuotaOfferModal,
            promoId: PROMO_ID,
            onShow: () => {
                storeHelper.markAsShown(PROMO_ID);
                sendPaymentGa({
                    action: 'show',
                    name: RADAR_NAME,
                    eventCategory: ECategoryGa.modal,
                });
                emitAnalyticEvent(AnalyticEventNames.EXTEND_QUOTA_OFFER_SHOW);
                reduxStore.dispatch(promoShown(EPromoType.extendQuotaOfferModal));
            },
            onPrimaryClick() {
                sendPaymentGa({
                    action: 'click',
                    name: RADAR_NAME,
                    eventCategory: ECategoryGa.modal,
                });
                emitAnalyticEvent(AnalyticEventNames.EXTEND_QUOTA_OFFER_PRIMARY_CLICK);
                // это не полный обработчик, остальное в show-функции
            },
            onSecondaryClick() {
                sendPaymentGa({
                    action: 'click-tariff',
                    name: RADAR_NAME,
                    eventCategory: ECategoryGa.modal,
                });
                emitAnalyticEvent(AnalyticEventNames.EXTEND_QUOTA_OFFER_SECONDARY_CLICK);
                // это не полный обработчик, остальное в show-функции
            },
            onClose: () => {
                reduxStore.dispatch(removePromo(EPromoType.extendQuotaOfferModal));
            },
            onCancel: () => {
                sendPaymentGa({
                    action: 'close',
                    name: RADAR_NAME,
                    eventCategory: ECategoryGa.modal,
                });
                emitAnalyticEvent(AnalyticEventNames.EXTEND_QUOTA_OFFER_CLOSE);
            },
        })
    );
}

export function* showExtendQuotaOfferModal() {
    const promo = yield* select(PromoSelectors.getPromo(EPromoType.extendQuotaOfferModal));
    const isMobile = EnvironmentSelectors.isMobile();

    if (!promo) {
        return;
    }

    const { onShow, onPrimaryClick: onPrimaryClickFirst, onSecondaryClick: onSecondaryClickFirst, onClose, onCancel } = promo;

    const suggest = extendQuotaOfferTariffs;
    const subsTariffs = yield loadSubscriptionProducts();
    const hasMonth = hasMonthTariff(subsTariffs);
    const productId = hasMonth ? suggest.month : suggest.year;
    const product = ProductsSelectors.getProductById(reduxStore.getState(), productId);
    const title = product && getExtentQuotaOfferTitle(isMobile, product);

    /* 
        дополняем действия тут, а не в инициализации, так как suggestProduct может дергать ручки,
        а дергать их, если промка даже не покажется, не надо
    */
    const onPrimaryClick = () => {
        if (isMobile) {
            closePopupHelper(popupNames.EXTEND_QUOTA_OFFER_MOBILE_MODAL);
            onClose();
        }

        onPrimaryClickFirst();
        reduxStore.dispatch(
            openFastCheckout({
                hideSwitchSidebarBlock: true,
                productId,
                paySource: PAY_SOURCE,
                // если title=undefined, то окно оплаты само его вычислит
                title,
            })
        );
    };

    const onSecondaryClick = () => {
        if (isMobile) {
            closePopupHelper(popupNames.EXTEND_QUOTA_OFFER_MOBILE_MODAL);
            onClose();
        }

        onSecondaryClickFirst();
        goToPromoQuota({
            currentPage: true,
            query: PaymentUTM.extendQuotaOfferPromo,
        });
    };

    yield preloadExtentQuotaOfferCoverImage();

    yield call(isMobile ? renderExtendQuotaOfferMobileModal : renderExtendQuotaOfferModal, {
        onShow,
        onPrimaryClick,
        onSecondaryClick,
        onClose,
        onCancel,
    });
}
